import Big from 'big.js';
import { HasPerm } from 'smg/admin/session';
import { SetupAdditionalDetails } from '../checkout/additionaldetails';
import { SameAvailableDiscountChoice, SetupAvailableDiscount } from '../checkout/available-discount';
import { SetupBalance } from '../checkout/balance';
import { GetCheckoutCart } from '../checkout/cart';
import { SetupCartWatcher } from '../checkout/cartwatcher';
import { ContactHandle, SetupContact } from '../checkout/contact';
import { SetupLineItemEditor } from '../checkout/lineitemeditor';
import { SetupPayment } from '../checkout/payment';
import { SetupPlaceOrder } from '../checkout/place';
import { SetupPrepayment } from '../checkout/prepayment';
import { SetupPromotional } from '../checkout/promotional';
import { SetupShipping } from '../checkout/shipping';
import { SetupShippingOverride } from '../checkout/shippingoverride';
import { SetupSummary } from '../checkout/summary';
import { SetupTaxExemption } from '../checkout/taxexemption';
import * as realms from '../realms';
import * as cartID from '../cart/cartid';
import { ShareCart } from '../cart/cartactions';
import { Relref } from '../hugo';

async function main() {
    if (!await HasPerm('r/carts')) {
        window.location.replace(Relref('/'));
        return;
    }

    const cart = await GetCheckoutCart();

    const $realm = document.getElementById('contact-realm') as HTMLSelectElement;

    // Per-item line item editor (qty / unit price). Edits live in the
    // editor's memory and ride both the summary's preview fetches and the
    // place-order POST — the cart and quote rows are never modified.
    // onChange only fires from the editor modal's Apply, which can only
    // happen after summary below exists and rendered the rows.
    const lineItemEditor = SetupLineItemEditor(cart, {
        onChange: () => summary.refreshEdits(),
    });
    const summary = SetupSummary(cart, lineItemEditor);
    const taxExemption = SetupTaxExemption({
        onExemptChange: exempt => summary.setExempt(exempt),
        // Contact owns the realm select; we read it here at the page wiring
        // boundary so the tax module doesn't have to know it exists.
        getCompanyID: () => $realm.value || undefined,
    });
    const payment = SetupPayment({
        getCompanyID: () => $realm.value || undefined,
        // Splits validate against the displayed grand total (includes the
        // exempt override). Summary owns the math; payment reads it.
        getOrderTotal: () => summary.getTotal(),
        // Mirror the payment module's "as-if-submitted" paid amount into
        // the summary so the order sidebar can show paid / remaining live.
        onPaidChange: paid => summary.setPaid(paid),
    });
    // Card amounts compare against summary's total; shipping/tax-exempt
    // moves can change the total without any payment input event, so push
    // the warning refresh from summary's side.
    summary.onTotalChange(() => payment.refreshWarning());
    const shipping = SetupShipping({
        onAddressChange: addr => {
            summary.refresh(addr);
            taxExemption.setShippingDestination(addr);
            payment.setShippingDestination(addr);
        },
    });
    // Admin shipping cost override (summary pencil dropdown). Forwarded to
    // summary so the next /carts/:id fetch carries shippingOverride and tax
    // recomputes against it — jurisdictions that tax shipping see the
    // override naturally.
    SetupShippingOverride({
        onOverrideChange: override => summary.setShippingOverride(override),
    });
    // User-driven account-balance credit. Deliberately not auto-applied
    // (per design) so shipping/tax/discount/exempt moves don't fight the
    // applied amount — the admin clicks "Apply $X" or types into the input.
    // The Apply link's suggestion does re-clamp on total changes so a click
    // always fills in a value that matches the live cart.
    const balance = SetupBalance({
        getTotalBeforeBalance: () => summary.getTotalBeforeBalance(),
        onAppliedChange: applied => summary.setBalanceApplied(applied),
        onTotalChange: cb => summary.onTotalChange(cb),
    });
    // Defaults to spending the customer's whole available discount (the
    // choice is omitted, which is the server's spend-all). Sales can turn
    // it off or type less. The choice rides the cart preview so the sidebar
    // total matches the order.
    const availableDiscount = SetupAvailableDiscount({
        onChoiceChange: apply => summary.setAvailableDiscountChoice(apply),
    });

    // Background poll that reloads the page if the cart was edited elsewhere
    // (another tab, a cart-items API call, etc.). Mirrors the customer-facing
    // express checkout. Only starts when we have a real cart — the empty
    // state has nothing to watch. The place-order submit calls .stop() before
    // posting so a mid-submit poll can't reload the page out from under it.
    const cartWatcher = cart && cart.cart.cartItems.length > 0 ?
        SetupCartWatcher({
            cartID: cart.cart.id,
            initialSubtotal: cart.subtotal,
        }) :
        undefined;

    // Tracked here (not inside contact) so the promotional autocomplete can
    // exclude the customer's own referral codes without contact having to
    // expose its currentUserID state. Declared above place-order so the
    // SetupPlaceOrder deps closure (getUserID) doesn't hit the TDZ.
    let currentUserID = '';

    // Contact's draft handle (email/phone as typed) for the new-customer
    // path. A ref-object rather than `const contact = SetupContact(...)`
    // because SetupContact fires onCustomerChange synchronously, which
    // reaches place.refresh() → getCustomerEmail before the SetupContact
    // call below returns — a direct binding would still be in its TDZ at
    // that moment, so the deps closures guard with `?.` instead.
    const contact: { handle?: ContactHandle } = {};

    // Additional details has no live cross-section dependencies — the place
    // handler reads its snapshot (event date, notes, SMS, promotional
    // approval) at submit time.
    const additionalDetails = SetupAdditionalDetails();

    // Promotional has no live cross-section dependencies. Declared above
    // place-order so its snapshot (promotionalCodeID) can ride the POST body —
    // otherwise an applied promo discounts the preview + payment splits but the
    // persisted order is created at full price.
    const promotional = SetupPromotional({
        getNetworkID: () => realms.SelectedNetworkID($realm),
        getCompanyID: () => $realm.value || '',
        getCustomerUserID: () => currentUserID,
        onPromoChange: code => summary.setPromoCode(code),
    });

    // The list of deposit orders is fixed for the session (cart items are
    // fixed during checkout, so the linked project set is fixed), so we
    // render it once. The applied amount, however, is capped by the
    // pre-prepayment total, which moves with shipping/promo/discount —
    // push the fresh capped value into the header on every summary
    // recompute. Each row's checkbox (checked by default) feeds the
    // selection back into the summary so unchecking a deposit immediately
    // shrinks the prepayment line and total.
    const prepayment = SetupPrepayment({
        onSelectionChange: checkedPaid => summary.setPrepaymentSelection(checkedPaid),
    });
    prepayment.refresh(cart?.pendingDepositOrders, cart?.prepayment ?? Big(0));
    summary.onPrepaymentChange(applied => prepayment.setApplied(applied));

    // Place-order has to exist before contact so the initial cart-customer
    // resolve (which fires onCustomerChange synchronously during SetupContact)
    // can call place.refresh() and enable the button on first paint.
    const place = SetupPlaceOrder({
        getCartID: () => cartID.Get(),
        getUserID: () => currentUserID,
        getCompanyID: () => $realm.value || '',
        getNetworkID: () => realms.SelectedNetworkID($realm),
        getPurchaseOrderNumbers: () => payment.getPurchaseOrderNumbers(),
        getCustomerEmail: () => contact.handle?.email() ?? '',
        getCustomerPhone: async () => await contact.handle?.phone() ?? '',
        getNewCustomerDraft: () => contact.handle?.newCustomer() ?? { useShippingName: true, firstName: '', lastName: '', company: '' },
        getShippingOverride: () => summary.getShippingOverride(),
        getLineItemOverrides: () => lineItemEditor.snapshot(),
        getOrderShippingOption: () => shipping.snapshot(),
        getUserTaxExemptionOrder: () => taxExemption.snapshot(),
        getEventDate: () => additionalDetails.snapshot().eventDate,
        getSMSOptIn: () => additionalDetails.snapshot().sms,
        getPromotionalApproval: () => additionalDetails.snapshot().promotionalApproval,
        getNotes: () => additionalDetails.snapshot().notes,
        getPromotionalCode: () => promotional.snapshot().promotionalCodeID,
        getSkipDepositOrderIDs: () => prepayment.skippedDepositOrderIDs(),
        // Account balance is charged first (before the payment method); the
        // place handler reads it fresh on each retry so a declined balance can
        // be adjusted and re-applied.
        getBalanceDraft: () => balance.snapshot(),
        // The customer, the discount lookup, and the preview settle as one
        // state: a customer resolving or a lookup changing the choice while we
        // wait re-queues the preview, so loop until one pass sees nothing
        // change, then hand back what that preview priced.
        settlePricing: async () => {
            for (;;) {
                const userID = currentUserID;
                await availableDiscount.settled();
                const availableDiscountChoice = availableDiscount.snapshot();
                if (!await summary.settled()) {
                    return undefined;
                }
                if (userID === currentUserID && SameAvailableDiscountChoice(availableDiscountChoice, availableDiscount.snapshot()) && availableDiscount.isSettled()) {
                    return { userID, availableDiscountChoice };
                }
            }
        },
        // Payment charging lives in the payment module (it owns the card
        // iframes); the place handler drives it after the order is created.
        freezePaymentIframes: () => payment.freezeIframes(),
        thawPaymentIframes: () => payment.thawIframes(),
        chargePayment: chargeDeps => payment.chargePayment(chargeDeps),
        stopWatcher: () => cartWatcher?.stop(),
        startWatcher: () => cartWatcher?.start(),
    });

    contact.handle = SetupContact(cart, {
        onCustomerChange: userID => {
            currentUserID = userID;
            shipping.refresh(userID);
            taxExemption.refresh(userID);
            payment.refresh(userID);
            balance.refresh(userID);
            summary.setCustomer(userID);
            availableDiscount.refresh(userID);
            place.refresh();
        },
        // applyRealm is async (waits on realm options to load) and never
        // dispatches 'change' for its programmatic assignment, so we get
        // an explicit signal here. Covers both the deferred cart-customer
        // realm apply and the admin's manual realm-dropdown change.
        onRealmChange: () => {
            // The BlockChyp card iframes mount under the realm's tokenizing
            // key, so they can't come up until a realm lands (deferred
            // applyRealm on cart load) and must be rebuilt when it changes.
            payment.refreshRealm();
            place.refresh();
        },
        // Keystroke-level signal: onCustomerChange dedupes on userID, so
        // typing between two unresolved emails never fires it — but the
        // place button must track the raw email (new-customer checkout).
        onDraftChange: () => place.refresh(),
    });

    // Share the current cart — same modal/flow as the cart-modal and carts-page
    // Share action (ShareCart falls back to the stored cartID and validates it).
    // The button lives in the summary sidebar, hidden with #summary-content
    // until a cart loads, so it's only reachable when there's a cart to share.
    const $shareCartBtn = document.getElementById('share-cart-btn') as HTMLButtonElement;
    $shareCartBtn.addEventListener('click', e => {
        e.preventDefault();
        void ShareCart(cartID.Get());
    });
}

void main();
